import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, RefreshCw, Wand2 } from "lucide-react";

const STAGE_LABELS = { exploring: "Exploring", planning: "Planning", plans_underway: "Plans underway", builder_appointed: "Builder appointed", ready_to_proceed: "Ready to proceed" };
const STATUSES = ["new", "reviewing", "qualified", "consultation", "proposal_opportunity", "converted", "nurture", "lost"];

export default function LeadDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [lead, setLead] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get(`/leads/${id}`).then((r) => setLead(r.data)), [id]);
  useEffect(() => { load(); }, [load]);

  if (!lead) return <div className="p-12 text-muted-foreground">Loading…</div>;

  const setStatus = async (s) => {
    await api.patch(`/leads/${id}`, { status: s });
    load(); toast.success("Status updated");
  };
  const rescore = async () => {
    await api.post(`/leads/${id}/rescore`); load(); toast.success("Rescored");
  };
  const convert = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/leads/${id}/convert`);
      toast.success(`Converted to ${data.project_number}`);
      nav(`/projects/${data.project_id}`);
    } catch (e) { toast.error(formatErr(e) || "Convert failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="px-6 lg:px-14 py-10 lg:py-14 max-w-6xl" data-testid="lead-detail-page">
      <button onClick={() => nav("/leads")} className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-6">
        <ArrowLeft className="w-3 h-3" strokeWidth={1.5}/> Back to leads
      </button>

      <div className="flex justify-between items-end flex-wrap gap-4 mb-8">
        <div>
          <div className="overline">Lead · {lead.reference}</div>
          <h1 className="font-serif font-light mt-3" style={{ fontSize: "44px", lineHeight: 1.05 }}>
            {lead.first_name} {lead.last_name}
          </h1>
          <div className="text-muted-foreground mt-2">{lead.email} · {lead.phone}</div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <select data-testid="select-lead-status" value={lead.status} onChange={(e) => setStatus(e.target.value)} className="luxe-input">
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <button data-testid="btn-rescore-lead" onClick={rescore} className="btn-outline">
            <RefreshCw className="w-4 h-4" strokeWidth={1.5}/> Rescore
          </button>
          {lead.status !== "converted" && (
            <button data-testid="btn-convert-lead" onClick={convert} disabled={busy} className="btn-accent">
              <Wand2 className="w-4 h-4" strokeWidth={1.5}/> {busy ? "Converting…" : "Convert to Project"}
            </button>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr,320px] gap-8">
        <div className="space-y-8">
          <Section title="Property">
            <KV k="Address" v={[lead.address, lead.suburb, lead.state, lead.postcode].filter(Boolean).join(", ") || "—"}/>
          </Section>
          <Section title="Project">
            <KV k="Type" v={lead.project_type || "—"}/>
            <KV k="Stage" v={STAGE_LABELS[lead.project_stage] || lead.project_stage || "—"}/>
            <KV k="Budget" v={lead.budget_band || "—"}/>
            <KV k="TV" v={lead.tv_size || "—"}/>
            <KV k="Fireplace" v={lead.fireplace || "—"}/>
            <KV k="Materials" v={(lead.material_preferences || []).join(", ") || "—"}/>
            <KV k="Styles" v={(lead.style_preferences || []).join(", ") || "—"}/>
            <KV k="Target timing" v={lead.target_timing || "—"}/>
          </Section>
          <Section title="Professionals">
            <KV k="Architect" v={lead.architect_involved ? (lead.architect_name || "Yes") : "—"}/>
            <KV k="Interior Designer" v={lead.interior_designer_involved ? (lead.interior_designer_name || "Yes") : "—"}/>
            <KV k="Builder" v={lead.builder_involved ? (lead.builder_name || "Yes") : "—"}/>
          </Section>
          {lead.comments && (
            <Section title="Comments">
              <div className="text-sm leading-relaxed">{lead.comments}</div>
            </Section>
          )}
          {lead.uploads?.length > 0 && (
            <Section title={`Uploads (${lead.uploads.length})`}>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {lead.uploads.map((u, i) => (
                  <div key={i} className="border border-border p-3 text-xs">
                    <div className="text-[10px] overline">{u.kind}</div>
                    <div className="mt-1 truncate">{u.original_filename}</div>
                  </div>
                ))}
              </div>
            </Section>
          )}
        </div>

        <aside className="space-y-4">
          <div className="luxe-card p-6">
            <div className="overline">Score</div>
            <div className="font-serif text-5xl mt-2">{lead.score ?? "—"}</div>
            <div className="text-xs mt-1">
              <span className="uppercase tracking-widest text-[color:hsl(var(--accent))]">{lead.classification}</span>
            </div>
            <div className="mt-5 space-y-2">
              {(lead.score_reasons || []).map((r, i) => (
                <div key={i} className="flex justify-between text-xs">
                  <div>
                    <div className="font-medium">{r.factor}</div>
                    <div className="text-muted-foreground">{STAGE_LABELS[r.detail] || r.detail}</div>
                  </div>
                  <div className="font-mono">+{r.points}</div>
                </div>
              ))}
              {(!lead.score_reasons || lead.score_reasons.length === 0) && <div className="text-xs text-muted-foreground">No score factors matched.</div>}
            </div>
          </div>
          {lead.converted_project_id && (
            <div className="luxe-card p-6">
              <div className="overline">Converted</div>
              <button data-testid="btn-goto-project" onClick={() => nav(`/projects/${lead.converted_project_id}`)} className="btn-outline mt-3 w-full justify-center">
                Open project <ArrowRight className="w-4 h-4" strokeWidth={1.5}/>
              </button>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="luxe-card p-6">
      <div className="overline mb-3">{title}</div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}
function KV({ k, v }) {
  return (
    <div className="flex justify-between text-sm border-b border-border py-2 last:border-0">
      <span className="text-muted-foreground">{k}</span>
      <span className="font-medium text-right">{v}</span>
    </div>
  );
}
